import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { FormActions } from "@jbnl/shared-ui";
import { Keuzelijst } from "./Keuzelijst";
import {
  useKandidaten,
  useLosmakenBezetting,
  useVastzettenBezetting,
  type Bezetting,
  type Kandidaat,
} from "./usePlanning";

/** The ondersteuner picker: a loading state, or the search list with its markers. */
function OndersteunerVeld({
  functieNaam,
  kandidaten,
  isPending,
  gekozen,
  onKies,
}: {
  functieNaam: string;
  kandidaten: Kandidaat[];
  isPending: boolean;
  gekozen: string;
  onKies: (id: string) => void;
}) {
  const { t } = useTranslation(["planning", "common"]);

  return (
    <Field>
      <FieldLabel>{t("bezettingDialog.ondersteuner")}</FieldLabel>
      <p className="text-xs text-muted-foreground">
        {t("bezettingDialog.ondersteunerUitleg", { functie: functieNaam })}
      </p>
      {isPending ? (
        <p className="text-sm text-muted-foreground">
          {t("bezettingDialog.ophalen")}
        </p>
      ) : (
        <Keuzelijst
          naam={t("keuzelijst.ondersteuner")}
          zoekPlaceholder={(count) =>
            t("keuzelijst.zoekOndersteuners", { count })
          }
          items={kandidaten}
          sleutel={(k) => k.persoonId}
          zoekterm={(k) => k.naam}
          gekozen={gekozen}
          onKies={onKies}
          regel={(k) => (
            <>
              {k.naam}
              {/* Marked rather than filtered: both constraints may be overruled, but neither should
                happen without the beheerder noticing which one they picked. */}
              {!k.heeftAangeboden && (
                <span className="text-muted-foreground">
                  {t("bezettingDialog.nietAangemeld")}
                </span>
              )}
              {k.isAlBezet && (
                <span className="text-muted-foreground">
                  {t("bezettingDialog.alIngezet")}
                </span>
              )}
            </>
          )}
          leeg={t("bezettingDialog.leeg")}
        />
      )}
    </Field>
  );
}

/**
 * The list is everybody, signups first: §4.4.4 lets a beheerder seat anybody, and the case they
 * need it for is the jurylid who never filled the form in. Placed and then reported, not refused
 * (ADR-0038 constraint 6).
 */
export function BezettingDialog({
  seizoenId,
  dagId,
  doel,
  doelId,
  doelLabel,
  bezetting,
  onClose,
}: {
  seizoenId: string;
  dagId: string;
  doel: number;
  doelId: string;
  doelLabel: string;
  bezetting: Bezetting;
  onClose: () => void;
}) {
  const { t } = useTranslation(["planning", "common"]);
  const { data: kandidaten, isPending } = useKandidaten(
    dagId,
    bezetting.functieId,
  );
  const vastzetten = useVastzettenBezetting(seizoenId);
  const losmaken = useLosmakenBezetting(seizoenId);

  const [persoonId, setPersoonId] = useState(bezetting.persoonId ?? "");
  const [reden, setReden] = useState(bezetting.reden ?? "");

  const bezig = vastzetten.isPending || losmaken.isPending;
  const fout = vastzetten.error ?? losmaken.error;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{bezetting.functieNaam}</DialogTitle>
          <DialogDescription>
            {bezetting.persoonNaam
              ? t("bezettingDialog.beschrijvingBezet", {
                  doel: doelLabel,
                  persoon: bezetting.persoonNaam,
                })
              : t("bezettingDialog.beschrijvingVrij", { doel: doelLabel })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <OndersteunerVeld
            functieNaam={bezetting.functieNaam}
            kandidaten={kandidaten ?? []}
            isPending={isPending}
            gekozen={persoonId}
            onKies={setPersoonId}
          />

          <Field>
            <FieldLabel htmlFor="reden-bezetting">
              {t("bezettingDialog.reden")}
            </FieldLabel>
            <Textarea
              id="reden-bezetting"
              value={reden}
              onChange={(event) => setReden(event.target.value)}
              placeholder={t("bezettingDialog.redenPlaceholder")}
              rows={2}
            />
            <p className="text-xs text-muted-foreground">
              {t("bezettingDialog.redenUitleg")}
            </p>
          </Field>

          {fout && <p className="text-sm text-destructive">{fout.message}</p>}

          <FormActions
            leading={
              bezetting.isVastgezet ? (
                <Button
                  variant="outline"
                  disabled={bezig}
                  onClick={() =>
                    losmaken.mutate(
                      { doel, doelId, functieId: bezetting.functieId },
                      { onSuccess: onClose },
                    )
                  }
                >
                  {t("bezettingDialog.losmaken")}
                </Button>
              ) : undefined
            }
          >
            <Button variant="ghost" onClick={onClose} disabled={bezig}>
              {t("bezettingDialog.annuleren")}
            </Button>
            <Button
              disabled={bezig || !persoonId}
              onClick={() =>
                vastzetten.mutate(
                  {
                    doel,
                    doelId,
                    functieId: bezetting.functieId,
                    persoonId,
                    reden: reden.trim() || null,
                  },
                  { onSuccess: onClose },
                )
              }
            >
              {bezig
                ? t("common:actions.busy")
                : t("bezettingDialog.vastzetten")}
            </Button>
          </FormActions>
        </div>
      </DialogContent>
    </Dialog>
  );
}
